import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { RotateCcwIconComponent } from '../../../@icons/rotate-ccw-icon.component';
import { CheckIconComponent } from '../../../@icons/check-icon.component';

/** Le temps laissé pour « Annuler », en millisecondes. */
export const UNDO_MS = 5000;

/**
 * Le message bref après un geste coché (maquette 05) : « Bravo, c'est noté »
 * et « Annuler », au-dessus de la barre du bas.
 *
 * `closed` dit à l'écran que l'action est acquise : au bout de `durationMs` (`UNDO_MS` par défaut), au
 * premier toucher hors « Annuler », ou quand l'écran s'en va. Une seule fois
 * par action, et jamais après `undo`. Les deux portent la `key` de l'action
 * qu'ils règlent : une nouvelle `key` règle la précédente si elle attend
 * encore, puis repart pour `UNDO_MS`.
 *
 * Tant que le focus est dans le message (clavier, lecteur d'écran), le délai
 * s'arrête : on a le temps d'atteindre « Annuler ».
 *
 * `overSheet` le pose au-dessus d'une feuille du bas ouverte (une ligne réglée
 * dans la feuille) : sans lui, la feuille le couvre.
 *
 * `bottomOffset` le pose au-dessus d'une autre barre du bas que celle de l'app
 * (la barre « Planter » de l'assistant d'ajout) ; sans lui, sa place habituelle.
 */
@Component({
  selector: 'vw-undo-snackbar',
  standalone: true,
  imports: [TranslatePipe, CheckIconComponent, RotateCcwIconComponent],
  templateUrl: './undo-snackbar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class UndoSnackbarComponent implements OnChanges, OnInit, OnDestroy {
  /** Texte déjà traduit. */
  @Input() message = '';
  /** L'action en attente (uid de la tâche ou de la plante). */
  @Input() key = '';
  /** Au-dessus des feuilles du bas (`vw-toast-popup`, 999999). */
  @Input() overSheet = false;
  /** Temps laissé pour « Annuler » ; 6 s pour une réponse à l'assistant. */
  @Input() durationMs = UNDO_MS;
  /** Distance au bas de l'écran (longueur CSS, `calc()` compris) ; `null` = au-dessus de la barre de l'app. */
  @Input() bottomOffset: string | null = null;
  @Output() undo = new EventEmitter<string>();
  @Output() closed = new EventEmitter<string>();

  @ViewChild('undoButton', { static: true }) undoButton?: ElementRef<HTMLButtonElement>;

  private timer?: ReturnType<typeof setTimeout>;
  private deadline = 0;
  /** Temps restant pendant une pause du délai, `null` quand il court. */
  private remaining: number | null = null;
  private settled = false;

  ngOnChanges(changes: SimpleChanges) {
    const key = changes['key'];
    if (key && !key.firstChange) {
      this.close(key.previousValue);
      this.start();
    }
  }

  ngOnInit() {
    this.start();
  }

  ngOnDestroy() {
    this.close(this.key);
  }

  /** Un toucher ailleurs vaut accord : l'action part. */
  @HostListener('document:pointerdown', ['$event'])
  onPointerDown(event: PointerEvent) {
    if (!this.undoButton?.nativeElement.contains(event.target as Node)) this.close(this.key);
  }

  onUndo() {
    if (this.settle()) this.undo.emit(this.key);
  }

  /** Le focus entre : le délai s'arrête. */
  onFocusIn() {
    if (this.settled || this.remaining !== null) return;
    clearTimeout(this.timer);
    this.remaining = Math.max(0, this.deadline - Date.now());
  }

  /** Le focus quitte le message : le délai reprend là où il s'était arrêté. */
  onFocusOut(event: FocusEvent) {
    const container = event.currentTarget as HTMLElement;
    if (this.settled || this.remaining === null || container.contains(event.relatedTarget as Node)) return;
    this.run(this.remaining);
  }

  private start() {
    this.settled = false;
    this.run(this.durationMs);
  }

  private run(ms: number) {
    this.remaining = null;
    this.deadline = Date.now() + ms;
    this.timer = setTimeout(() => this.close(this.key), ms);
  }

  private close(key: string) {
    if (this.settle()) this.closed.emit(key);
  }

  /** Vrai au premier dénouement de l'action (annulée ou fermée) ; les suivants ne comptent plus. */
  private settle(): boolean {
    if (this.settled) return false;
    this.settled = true;
    clearTimeout(this.timer);
    return true;
  }
}
